import React from 'react';
import { Button, Dropdown } from 'antd';
import { Link } from 'react-router-dom';

import { RecordIconTile, RecordTileStack } from './RecordBadge';
import { buildActionMenu } from './actionMenu';
import { useHelpAction } from '../../contexts/ShortcutsContext';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import useWindowClass from '../../hooks/useWindowClass';
import { KEBAB } from '../icons';

/**
 * THE context bar: one per route, sticky at the top of the work area.
 *
 *   ┌ chip ┐  breadcrumb  (Collections / Biewend AFH)          [secondaries] [⋯] [Primary]
 *   └──────┘  Object title (21px/600)
 *             below (meta, state)
 *   ─────────────────────────────────────────────────────────────────────────
 *   children — the optional second row: a list's filter row, a settings
 *   surface's tabs (ContextBarTabs)
 *
 * The object's own surface shows chip + plain title — never a bordered badge
 * (the badge is for references made from elsewhere). The breadcrumb names
 * the containers above it (`Collections / {Collection} / {sub}`) and
 * replaces every "back to…" link; the collection a form belongs to is a
 * breadcrumb segment, not a second header.
 *
 *   <ContextBar
 *     breadcrumb={[{ label: 'Collections', to: '/collections' }, { label: collection.name, to: itemsPath }]}
 *     record={card} kind="card" title="Blood pressure"
 *     actions={<><Button>Cancel</Button><Button type="primary">Save</Button></>}
 *     menuActions={[{ key: 'export', group: 'share', glyph: 'export', label: 'Export…', onClick: openExport }]}
 *   />
 *
 * THE PAGE'S ⋯ IS THE BAR'S, not the page's: hand it `menuActions` (the flat
 * action objects buildActionMenu takes) and the bar draws the kebab at the
 * far right of the row — after the buttons, as the action grammar has it
 * (Draft saved · Cancel · Save · ⋯). The bar always appends the **Help**
 * group ("Keyboard shortcuts…"), and draws the kebab for that alone on a page
 * with no actions of its own, so every page carries the same door to help
 * without each one remembering to. A page that renders its own Dropdown
 * inside `actions` would get two — pass `menuActions` instead.
 *
 * THE BAR NAMES THE BROWSER TAB: a string `title` is the tab's title too
 * (`Blood pressure · keepr`, hooks/useDocumentTitle), so a page never sets
 * one of its own — a collection, a card and an item each get theirs by
 * having a bar.
 */
export default function ContextBar({
	breadcrumb = [],
	// A few words on WHAT the object is, when that is not the ordinary thing
	// ("Personal collection", "Global collection") — drawn on the breadcrumb
	// line after a middle dot, muted, never a link: it describes, it does not
	// navigate.
	kicker = null,
	record,
	// Several records at once (a multi add form: its steps' cards) — a tile
	// stack in the chip's place. Wins over `record`.
	records,
	kind = 'collection',
	title,
	titleAs: TitleTag = 'h1',
	// The card's name above the title, as a record badge wears it (§1): an
	// item's own bar under the collection shell says what KIND of record it is.
	eyebrow = null,
	// False when the shell above already names the browser tab.
	nameTab = true,
	titleTo,
	below = null,
	error = null,
	actions = null,
	menuActions = null,
	menuLabel = 'More actions',
	// The one page whose ⋯ cannot be the bar's: a collection's bar puts its
	// kebab BEFORE the primary button ([secondaries] [⋯] [Primary]), so it
	// draws its own and adds the Help entry through `useHelpAction`.
	ownMenu = false,
	children = null,
	className = '',
	style,
	tileSize = 48,
	...rest
}) {
	const crumbs = (Array.isArray(breadcrumb) ? breadcrumb : []).filter(Boolean);
	// compact (§18): a 36px chip beside a 17px title (the CSS shrinks the title).
	const { compact } = useWindowClass();
	const chipPx = compact ? Math.min(tileSize, 36) : tileSize;
	const titleNode = titleTo
		? <Link to={titleTo} className="context-bar__title-link">{title}</Link>
		: title;
	const classes = ['context-bar', className].filter(Boolean).join(' ');
	useDocumentTitle(nameTab && typeof title === 'string' ? title : '');

	// The Help group, on every page's menu. Without the provider (a component
	// rendered bare in a test) there is nothing to open, so the entry is left
	// off rather than offered dead — and a bar with neither actions nor a
	// provider draws no kebab at all.
	const helpAction = useHelpAction();
	const menu = ownMenu ? null : buildActionMenu([
		...(Array.isArray(menuActions) ? menuActions.filter(Boolean) : []),
		helpAction,
	].filter(Boolean));

	return (
		<header className={classes} style={style} {...rest}>
			<div className="context-bar__row">
				<div className="context-bar__identity">
					{Array.isArray(records) ? (
						<RecordTileStack records={records} kind={kind} size={chipPx} className="context-bar__tile" />
					) : record !== undefined ? (
						<RecordIconTile record={record} kind={kind} size={chipPx} className="context-bar__tile" />
					) : null}
					<div className="context-bar__titles">
						{crumbs.length || kicker ? (
							<div className="context-bar__crumb-line">
								{crumbs.length ? (
									<nav className="context-bar__breadcrumb" aria-label="Breadcrumb">
										{crumbs.map((crumb, index) => (
											<React.Fragment key={`${crumb.label}-${index}`}>
												{index > 0 ? <span className="context-bar__crumb-sep" aria-hidden="true">/</span> : null}
												{crumb.to ? <Link to={crumb.to}>{crumb.label}</Link> : <span>{crumb.label}</span>}
											</React.Fragment>
										))}
									</nav>
								) : null}
								{kicker ? (
									<span className="context-bar__kicker">
										{crumbs.length ? <span className="context-bar__crumb-sep" aria-hidden="true">·</span> : null}
										{kicker}
									</span>
								) : null}
							</div>
						) : null}
						{eyebrow ? <span className="context-bar__eyebrow" data-item-eyebrow>{eyebrow}</span> : null}
						<TitleTag className="context-bar__title">{titleNode}</TitleTag>
						{below ? <div className="context-bar__below">{below}</div> : null}
						{error ? <small className="context-bar__error">{error}</small> : null}
					</div>
				</div>
				{(actions || menu) ? (
					<div className="context-bar__actions">
						{actions}
						{menu ? (
							<Dropdown trigger={['click']} placement="bottomRight" menu={menu}>
								<Button icon={<KEBAB />} aria-label={menuLabel} />
							</Dropdown>
						) : null}
					</div>
				) : null}
			</div>
			{children ? <div className="context-bar__row context-bar__row--secondary">{children}</div> : null}
		</header>
	);
}

/**
 * Tabs inside the context bar (settings surfaces, the admin portal): 13.5px
 * labels, the active one 600 with a 2px action underline. Navigation only —
 * the surface renders the active tab's content beneath the bar.
 */
export function ContextBarTabs({ items = [], activeKey, onChange, className = '', 'aria-label': ariaLabel = 'Sections' }) {
	return (
		<div role="tablist" aria-label={ariaLabel} className={['context-tabs', className].filter(Boolean).join(' ')}>
			{items.filter(Boolean).map(item => {
				const active = item.key === activeKey;
				return (
					<button
						key={item.key}
						type="button"
						role="tab"
						aria-selected={active}
						className={`context-tabs__tab${active ? ' context-tabs__tab--active' : ''}`}
						onClick={() => onChange?.(item.key)}
						disabled={item.disabled}
					>
						{item.icon ? <span className="context-tabs__icon" aria-hidden="true">{item.icon}</span> : null}
						{item.label}
					</button>
				);
			})}
		</div>
	);
}
